import { disc, hash, paintBands, paintMound, px, type GroundLook } from './art';

/**
 * Mars, painted in code: a dusty butterscotch sky with a small pale sun and Phobos, flat-topped
 * mesas, layered rock pillars nearer in, dark boulders and rocks passing in front. Rust haze. Every
 * scrolling layer tiles horizontally.
 */

type Ctx = CanvasRenderingContext2D;

export const MARS_COLORS = {
  skyBands: ['#2a1420', '#3c1a22', '#562624', '#74362a', '#925032', '#ad6a40', '#c48552'],
  sun: '#f4ead2',
  sunHalo: '#d9a77a',
  phobos: '#7a6460',
  phobosLit: '#a08a80',
  dust: '#d8b48a',
  haze: '#a8603e',

  mesa: '#5c2a22',
  mesaLit: '#80402c',
  mesaNear: '#4a2019',
  mesaNearLit: '#6c3424',
  pillar: '#3e1a14',
  pillarBand: '#58261b',
  pillarLit: '#74362a',

  fg: '#120706',
  fgLit: '#2a110d',
} as const;

const C = MARS_COLORS;

export const MARS_GROUND: GroundLook = {
  cap: 'dust',
  plank: 'metal',
  colors: {
    top: '#c4643a',
    topLit: '#ea9464',
    topDark: '#94482a',
    body: '#76381f',
    bodyDark: '#562816',
    speck: '#9a5030',
    ink: '#1a0a08',
    plank: '#7a6a62',
    plankLit: '#b0a094',
    plankDark: '#463a36',
    hang: null,
  },
};

/** Banded dusty sky, a few faint stars high up, a small sun with a halo, Phobos. */
export function paintMarsSky(ctx: Ctx, width: number, height: number): void {
  paintBands(ctx, C.skyBands, width, height);
  for (let i = 0; i < 40; i++) {
    px(ctx, C.dust, Math.floor(hash(i, 1, 51) * width), Math.floor(hash(i, 2, 51) * height * 0.3));
  }
  // The sun is smaller out here. Placed to show on the narrowest (480 px) screen.
  const sx = Math.min(width - 80, 360);
  disc(ctx, sx, 64, 11, C.sunHalo);
  disc(ctx, sx, 64, 6, C.sun);
  // Phobos: a lumpy little moon.
  disc(ctx, 96, 34, 5, C.phobos);
  disc(ctx, 95, 33, 3, C.phobosLit);
  px(ctx, C.phobos, 97, 31, 2, 1);
}

/** Flat-topped mesas with steep sides, far and near. */
export function paintMarsMesas(ctx: Ctx, width: number, height: number): void {
  const layer = (
    salt: number,
    count: number,
    low: number,
    high: number,
    body: string,
    lit: string,
  ) => {
    const tops = new Array<number>(width).fill(height);
    for (let i = 0; i < count; i++) {
      const cx = Math.floor(hash(i, 1, salt) * width);
      const half = 20 + Math.floor(hash(i, 2, salt) * 50);
      const top = low - Math.floor(hash(i, 3, salt) * (low - high));
      for (let dx = -half - 12; dx <= half + 12; dx++) {
        // Steep talus slopes on either side of the flat top.
        const slope = Math.max(0, Math.abs(dx) - half);
        const y = top + slope * 4;
        const x = (((cx + dx) % width) + width) % width;
        tops[x] = Math.min(tops[x] ?? height, y);
      }
    }
    for (let x = 0; x < width; x++) {
      const top = tops[x] ?? height;
      if (top >= height) continue;
      px(ctx, body, x, top, 1, height - top);
      if ((tops[x - 1] ?? height) > top || x === 0) px(ctx, lit, x, top, 1, 2);
      else px(ctx, lit, x, top, 1, 1);
    }
  };
  // A low plain runs under both rows of mesas.
  px(ctx, C.mesaNear, 0, height - 70, width, 70);
  layer(61, 5, 170, 120, C.mesa, C.mesaLit);
  layer(67, 4, 210, 172, C.mesaNear, C.mesaNearLit);
}

/** Layered rock pillars (hoodoos) between the mesas and the lanes, strata in bands. */
export function paintMarsPillars(ctx: Ctx, width: number, height: number): void {
  const pillars = [
    { x: 30, w: 26, top: 120 },
    { x: 150, w: 18, top: 160 },
    { x: 228, w: 34, top: 96 },
    { x: 330, w: 20, top: 150 },
  ];
  for (const p of pillars) {
    for (let y = p.top; y < height; y++) {
      // Wider at the base, with a cap that overhangs a little.
      const flare = y > height - 40 ? Math.floor((y - (height - 40)) / 3) : 0;
      const cap = y < p.top + 6 ? 2 : 0;
      const x0 = p.x - flare - cap;
      const w = p.w + flare * 2 + cap * 2;
      const band = Math.floor((y - p.top) / 7) % 2 === 0 ? C.pillar : C.pillarBand;
      px(ctx, band, x0, y, w, 1);
      px(ctx, C.pillarLit, x0, y, 2, 1);
    }
    px(ctx, C.pillarLit, p.x - 2, p.top, p.w + 4, 1);
  }
  // Rubble along the bottom.
  for (let x = 0; x < width; x++) {
    const top =
      height - 30 + Math.round(4 * Math.sin((x / width) * Math.PI * 6) + 3 * hash(x, 7, 69));
    px(ctx, C.pillar, x, top, 1, height - top);
  }
}

/** Dark boulders and a wind-cut rock passing in front. */
export function paintMarsForeground(ctx: Ctx, width: number, height: number): void {
  const rock = (cx: number, r: number) => paintMound(ctx, cx, r, 1.2, height, C.fg, C.fgLit);
  rock(Math.round(width * 0.08), 18);
  rock(Math.round(width * 0.46), 30);
  rock(Math.round(width * 0.52), 16);
  // A wind-cut rock with a ledge, broader than the boulders.
  const sx = Math.round(width * 0.8);
  for (let i = 0; i < 64; i++) {
    const w = 26 - Math.round(i / 5) + (i > 40 && i < 46 ? 8 : 0);
    px(ctx, C.fg, sx - Math.floor(w / 2) + Math.floor(i / 10), height - i, w, 1);
  }
  px(ctx, C.fgLit, sx - 6, height - 46, 12, 1);
}
